import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Home,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Plus,
  MapPin,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Building2,
  X,
  Trash2,
  User,
  Scale,
  Landmark,
  Shield,
  Briefcase,
  ClipboardList,
  Receipt,
  Send,
  Clock,
  FileCheck,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  realEstateCasesApi,
  realEstateProgramsApi,
  clientsApi,
  usersApi,
  invoicesApi,
  taxSettingsApi,
} from "../../services/api";
import {
  calculateTaxCents,
  getTaxLabel,
  DEFAULT_TAX_CONFIG,
  TaxConfig,
  TaxBreakdown,
} from "../../utils/taxCalculation";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import QuickCreateClientModal from "../ui/QuickCreateClientModal";
import { useUserDataContext } from "../../context/UserDataContext";
import Toggle from "../ui/Toggle";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
  onCreated: (
    id: string,
    meta?: {
      clientId: string;
      clientDisplayName: string;
      matterTitle: string;
      matterId?: string;
    }
  ) => void;
}

interface ClientParty {
  clientId: string;
  role: string;
  capacityOnTitle: string;
}

interface Condition {
  label: string;
  waived: boolean;
}

interface ChecklistItem {
  key: string;
  label: string;
  required: boolean;
  source: "client" | "other_side" | "firm";
  enabled: boolean;
  isCustom: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STEPS = [
  { num: 1, label: "Type" },
  { num: 2, label: "Jurisdiction" },
  { num: 3, label: "Property" },
  { num: 4, label: "Parties" },
  { num: 5, label: "Dates & APS" },
  { num: 6, label: "Financial" },
  { num: 7, label: "Checklist & Billing" },
  { num: 8, label: "Review" },
];

const SOURCE_CONFIG = {
  client: { label: "Client", Icon: User, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-700" },
  other_side: { label: "Other Side", Icon: Users, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-700" },
  firm: { label: "Firm", Icon: Briefcase, color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200", badge: "bg-purple-100 text-purple-700" },
} as const;

type InvoiceTiming = "now" | "on_document_received" | "manual";

const TRANSACTION_TYPES = [
  {
    value: "purchase",
    label: "Purchase",
    icon: Home,
    description: "Buying residential or commercial property",
  },
  {
    value: "sale",
    label: "Sale",
    icon: DollarSign,
    description: "Selling residential or commercial property",
  },
  {
    value: "mortgage",
    label: "Mortgage",
    icon: Landmark,
    description: "New mortgage registration on property",
  },
  {
    value: "refinance",
    label: "Refinance",
    icon: Scale,
    description: "Refinancing an existing mortgage",
  },
  {
    value: "private_lending",
    label: "Private Lending",
    icon: Shield,
    description: "Private mortgage or lending arrangement",
  },
];

const COUNTRY_OPTIONS = [
  { value: "CA", label: "Canada" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
];

const CA_PROVINCES = [
  { value: "ON", label: "Ontario" },
  { value: "BC", label: "British Columbia" },
  { value: "AB", label: "Alberta" },
  { value: "QC", label: "Quebec" },
  { value: "MB", label: "Manitoba" },
  { value: "SK", label: "Saskatchewan" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NB", label: "New Brunswick" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NT", label: "Northwest Territories" },
  { value: "YT", label: "Yukon" },
  { value: "NU", label: "Nunavut" },
];

const ROLE_OPTIONS = [
  { value: "purchaser", label: "Purchaser" },
  { value: "vendor", label: "Vendor" },
  { value: "borrower", label: "Borrower" },
  { value: "lender", label: "Lender" },
  { value: "guarantor", label: "Guarantor" },
];

const DEPOSIT_HELD_BY_OPTIONS = [
  { value: "broker", label: "Broker" },
  { value: "lawyer", label: "Lawyer" },
  { value: "other", label: "Other" },
];

const TRUST_CLASSIFICATION_OPTIONS = [
  { value: "retainer", label: "Retainer" },
  { value: "closing_funds", label: "Closing Funds" },
  { value: "mixed", label: "Mixed" },
];

const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 20 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -20 }),
};

const INPUT_CLASS =
  "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors";

// ─── LTT Calculators ────────────────────────────────────────────────────────

function calculateOntarioLTT(priceCents: number): number {
  const price = priceCents / 100;
  let ltt = 0;
  if (price > 400000) ltt += (price - 400000) * 0.02;
  if (price > 250000) ltt += (Math.min(price, 400000) - 250000) * 0.015;
  if (price > 55000) ltt += (Math.min(price, 250000) - 55000) * 0.01;
  if (price > 0) ltt += Math.min(price, 55000) * 0.005;
  return Math.round(ltt * 100);
}

function calculateTorontoMLTT(priceCents: number): number {
  const price = priceCents / 100;
  let mltt = 0;
  if (price > 400000) mltt += (price - 400000) * 0.02;
  if (price > 55000) mltt += (Math.min(price, 400000) - 55000) * 0.01;
  if (price > 0) mltt += Math.min(price, 55000) * 0.005;
  return Math.round(mltt * 100);
}

const formatCAD = (cents: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);

// ─── Component ───────────────────────────────────────────────────────────────

const CreateRealEstateMatterWizard: React.FC<Props> = ({
  onClose,
  onCreated,
}) => {
  const { userData } = useUserDataContext();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  // Data
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  // Review section collapse state
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    type: true,
    jurisdiction: true,
    property: true,
    parties: true,
    dates: true,
    financial: true,
    checklist: true,
    invoice: true,
  });

  // ── Step 1: Transaction Type ──
  const [transactionType, setTransactionType] = useState("");

  // ── Step 2: Jurisdiction ──
  const [country, setCountry] = useState("CA");
  const [province, setProvince] = useState("ON");
  const [municipality, setMunicipality] = useState("");

  // ── Step 3: Property ──
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [pins, setPins] = useState<string[]>([""]);
  const [legalDescription, setLegalDescription] = useState("");
  const [isCondo, setIsCondo] = useState(false);
  const [condoCorp, setCondoCorp] = useState({
    name: "",
    unitNumber: "",
    parkingUnit: "",
    lockerUnit: "",
    propertyManagerName: "",
    propertyManagerEmail: "",
    propertyManagerPhone: "",
  });

  // ── Step 4: Parties ──
  const [clientParties, setClientParties] = useState<ClientParty[]>([]);
  const [otherSideCounsel, setOtherSideCounsel] = useState({
    firmName: "",
    lawyerName: "",
    email: "",
    phone: "",
  });
  const [realtor, setRealtor] = useState({
    name: "",
    brokerage: "",
    email: "",
    phone: "",
  });
  const [lender, setLender] = useState({
    name: "",
    branchTransit: "",
  });

  // ── Step 5: Dates & APS ──
  const [closingDate, setClosingDate] = useState("");
  const [apsDate, setApsDate] = useState("");
  const [requisitionDate, setRequisitionDate] = useState("");
  const [possessionDate, setPossessionDate] = useState("");
  const [purchasePriceDollars, setPurchasePriceDollars] = useState("");
  const [depositAmountDollars, setDepositAmountDollars] = useState("");
  const [depositHeldBy, setDepositHeldBy] = useState("");
  const [conditions, setConditions] = useState<Condition[]>([]);

  // ── Step 6: Financial ──
  const [firstTimeHomeBuyer, setFirstTimeHomeBuyer] = useState(false);
  const [trustClassification, setTrustClassification] = useState("");
  const [holdbackNotes, setHoldbackNotes] = useState("");

  // ── Step 7: Checklist & Billing ──
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState("");
  const [newItemSource, setNewItemSource] = useState<"client" | "other_side" | "firm">("client");
  const [collapsedSources, setCollapsedSources] = useState<Record<string, boolean>>({});

  // Invoice setup
  const [createInvoice, setCreateInvoice] = useState(false);
  const [invoiceFeeDollars, setInvoiceFeeDollars] = useState("");
  const [invoiceTiming, setInvoiceTiming] = useState<InvoiceTiming>("manual");
  const [invoiceTriggerKey, setInvoiceTriggerKey] = useState("");
  const [dueDateOption, setDueDateOption] = useState<"0" | "15" | "30" | "custom">("30");
  const [customDueDate, setCustomDueDate] = useState("");

  // ── Data Fetching ──
  useEffect(() => {
    Promise.all([
      clientsApi.list({ limit: 500 }),
      usersApi.list({ limit: 200 }),
      realEstateProgramsApi.list(),
    ])
      .then(([cRes, uRes, pRes]) => {
        setClients((cRes.data as any).data || cRes.data || []);
        setUsers((uRes.data as any).data || uRes.data || []);
        setPrograms((pRes.data as any).data || pRes.data || []);
      })
      .catch(() => {});

    // Fetch firm tax settings for invoice tax calculation
    taxSettingsApi
      .get()
      .then((res) => {
        const data = res.data as any;
        if (data?.taxMode) {
          setTaxConfig({
            taxMode: data.taxMode,
            hstRate: data.hstRate ?? DEFAULT_TAX_CONFIG.hstRate,
            gstRate: data.gstRate ?? DEFAULT_TAX_CONFIG.gstRate,
            pstRate: data.pstRate ?? DEFAULT_TAX_CONFIG.pstRate,
          });
        }
      })
      .catch(() => {
        // Fall back to default (Ontario HST 13%)
      });
  }, []);

  // ── Derived Values ──
  const purchasePriceCents = Math.round(
    (parseFloat(purchasePriceDollars) || 0) * 100
  );
  const depositAmountCents = Math.round(
    (parseFloat(depositAmountDollars) || 0) * 100
  );

  const ontarioLTT =
    province === "ON" ? calculateOntarioLTT(purchasePriceCents) : 0;
  const torontoMLTT =
    province === "ON" &&
    municipality.toLowerCase().includes("toronto")
      ? calculateTorontoMLTT(purchasePriceCents)
      : 0;

  const provincialRebate = firstTimeHomeBuyer
    ? Math.min(ontarioLTT, 400000)
    : 0;
  const municipalRebate = firstTimeHomeBuyer
    ? Math.min(torontoMLTT, 447500)
    : 0;

  const showLender = ["purchase", "mortgage", "refinance"].includes(
    transactionType
  );

  // ── Checklist Loading from Program ──
  useEffect(() => {
    if (!transactionType || !province || programs.length === 0) return;
    // Find matching program
    const program = programs.find(
      (p: any) =>
        p.transactionType === transactionType &&
        p.jurisdiction?.province === province &&
        p.isActive !== false
    );
    if (program?.documentChecklistTemplates?.length > 0) {
      setChecklistItems(
        program.documentChecklistTemplates.map((t: any) => ({
          key: t.key,
          label: t.label,
          required: t.required ?? false,
          source: t.source || "client",
          enabled: true,
          isCustom: false,
        }))
      );
      setChecklistLoaded(true);
    } else {
      setChecklistItems([]);
      setChecklistLoaded(false);
    }
  }, [transactionType, province, programs]);

  // ── Checklist Derived ──
  const enabledChecklistItems = useMemo(
    () => checklistItems.filter((i) => i.enabled),
    [checklistItems]
  );

  const checklistBySource = useMemo(() => {
    const groups: Record<string, ChecklistItem[]> = { client: [], other_side: [], firm: [] };
    enabledChecklistItems.forEach((item) => {
      if (groups[item.source]) groups[item.source].push(item);
    });
    return groups;
  }, [enabledChecklistItems]);

  // ── Invoice Derived ──
  const invoiceFeeCents = Math.round((parseFloat(invoiceFeeDollars) || 0) * 100);

  const invoiceTaxBreakdown: TaxBreakdown = useMemo(
    () => calculateTaxCents(invoiceFeeCents, taxConfig),
    [invoiceFeeCents, taxConfig]
  );
  const invoiceTaxLabel = useMemo(() => getTaxLabel(taxConfig), [taxConfig]);
  const invoiceTotalCents = invoiceFeeCents + invoiceTaxBreakdown.totalTaxCents;

  const computeInvoiceDueDate = (): string => {
    const days = dueDateOption === "custom" ? 0 : parseInt(dueDateOption);
    if (dueDateOption === "custom") return customDueDate || new Date().toISOString().slice(0, 10);
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const triggerItem = checklistItems.find((i) => i.key === invoiceTriggerKey && i.enabled);

  // ── Navigation ──
  const goNext = () => {
    setDirection(1);
    setStep((s) => Math.min(s + 1, 8));
  };
  const goBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 1));
  };
  const goToStep = (target: number) => {
    setDirection(target > step ? 1 : -1);
    setStep(target);
  };

  const canProceed = (): boolean => {
    switch (step) {
      case 1:
        return !!transactionType;
      case 2:
        return !!country && !!province;
      case 3:
        return !!addressLine1.trim();
      case 4:
        return clientParties.length > 0 && clientParties.every((p) => p.clientId && p.role);
      case 5:
        return true;
      case 6:
        return true;
      case 7:
        return true;
      case 8:
        return true;
      default:
        return false;
    }
  };

  // ── Client Party Helpers ──
  const addClientParty = (clientId: string) => {
    if (clientParties.find((p) => p.clientId === clientId)) return;
    setClientParties((prev) => [
      ...prev,
      { clientId, role: "purchaser", capacityOnTitle: "" },
    ]);
  };

  const updateClientParty = (
    index: number,
    field: keyof ClientParty,
    value: string
  ) => {
    setClientParties((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const removeClientParty = (index: number) => {
    setClientParties((prev) => prev.filter((_, i) => i !== index));
  };

  const getClientName = (clientId: string) => {
    const c = clients.find((cl: any) => cl._id === clientId);
    return c?.displayName || "Unknown Client";
  };

  // ── PIN Helpers ──
  const addPin = () => setPins((prev) => [...prev, ""]);
  const updatePin = (index: number, value: string) =>
    setPins((prev) => prev.map((p, i) => (i === index ? value : p)));
  const removePin = (index: number) =>
    setPins((prev) => (prev.length === 1 ? [""] : prev.filter((_, i) => i !== index)));

  // ── Condition Helpers ──
  const addCondition = () =>
    setConditions((prev) => [...prev, { label: "", waived: false }]);
  const updateCondition = (
    index: number,
    field: keyof Condition,
    value: string | boolean
  ) =>
    setConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  const removeCondition = (index: number) =>
    setConditions((prev) => prev.filter((_, i) => i !== index));

  // ── Submit ──
  const handleCreate = async () => {
    // Guard: must have at least one client with a valid ID
    if (!clientParties[0]?.clientId || !transactionType) {
      setError("Please complete all required steps before creating the matter.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const payload = {
        clientId: clientParties[0]?.clientId || null,
        transactionType,
        jurisdiction: {
          country,
          province,
          municipality: municipality.trim() || null,
        },
        property: {
          address: {
            line1: addressLine1,
            line2: addressLine2 || null,
            city,
            region: province,
            postal: postalCode,
            country,
          },
          legalDescription: legalDescription || null,
          pins: pins.filter((p) => p.trim()),
          isCondo,
          condoCorp: isCondo
            ? {
                name: condoCorp.name,
                unitNumber: condoCorp.unitNumber,
                parkingUnit: condoCorp.parkingUnit || null,
                lockerUnit: condoCorp.lockerUnit || null,
                propertyManagerName: condoCorp.propertyManagerName || null,
                propertyManagerEmail: condoCorp.propertyManagerEmail || null,
                propertyManagerPhone: condoCorp.propertyManagerPhone || null,
              }
            : null,
        },
        parties: {
          clients: clientParties.map((p) => ({
            clientId: p.clientId,
            role: p.role,
            capacityOnTitle: p.capacityOnTitle || null,
          })),
          otherSideCounsel: otherSideCounsel.firmName
            ? otherSideCounsel
            : null,
          realtor: realtor.name ? realtor : null,
          lender: showLender && lender.name ? lender : null,
        },
        dates: {
          closingDate: closingDate || null,
          apsDate: apsDate || null,
          requisitionDate: requisitionDate || null,
          possessionDate: possessionDate || null,
        },
        apsTerms: {
          purchasePriceCents,
          depositAmountCents,
          depositHeldBy: depositHeldBy || null,
          conditions: conditions.filter((c) => c.label.trim()),
        },
        ltt: {
          firstTimeHomeBuyer,
        },
        compliance: {
          trustClassification: trustClassification || null,
        },
      };

      // Include edited document checklist (only enabled items)
      if (enabledChecklistItems.length > 0) {
        (payload as any).documentChecklist = enabledChecklistItems.map((item) => ({
          key: item.key,
          label: item.label,
          required: item.required,
          source: item.source,
        }));
      }

      // Include invoice setup
      if (createInvoice && invoiceFeeCents > 0) {
        (payload as any).invoice = {
          create: true,
          feeCents: invoiceFeeCents,
          timing: invoiceTiming,
          triggerChecklistKey: invoiceTiming === "on_document_received" ? invoiceTriggerKey : null,
          dueAt: computeInvoiceDueDate(),
        };
      }

      const res = await realEstateCasesApi.create(payload);
      const created = res.data;

      // If invoice timing is "now" and backend returned an invoiceId, send it
      if (createInvoice && invoiceTiming === "now" && created.invoiceId) {
        try {
          await invoicesApi.send(created.invoiceId);
        } catch {
          console.error("Invoice created but failed to send");
        }
      }

      const primaryClient = clientParties[0];
      onCreated(created._id, {
        clientId: primaryClient?.clientId || "",
        clientDisplayName: primaryClient
          ? getClientName(primaryClient.clientId)
          : "",
        matterTitle: `${transactionType.charAt(0).toUpperCase() + transactionType.slice(1).replace("_", " ")} - ${addressLine1}`,
        matterId: created.matterId,
      });
    } catch (err: any) {
      console.error("[RE Wizard] create failed:", err.response?.status, err.response?.data);
      const details = err.response?.data?.details;
      const serverError = err.response?.data?.error;
      if (Array.isArray(details) && details.length > 0) {
        setError(`${serverError || "Validation failed"}:\n${details.join("\n")}`);
      } else {
        setError(serverError || "Failed to create real estate matter");
      }
    } finally {
      setCreating(false);
    }
  };

  // ── Review Toggle ──
  const toggleSection = (key: string) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // ─── Step Progress Bar ─────────────────────────────────────────────────────

  const renderProgressBar = () => (
    <div className="flex items-center justify-between px-2 mb-6">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.num}>
          <div className="flex flex-col items-center gap-1.5">
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step > s.num
                  ? "bg-emerald-600 text-white"
                  : step === s.num
                  ? "bg-emerald-600 text-white ring-4 ring-emerald-100"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              {step > s.num ? <Check className="h-4 w-4" /> : s.num}
            </div>
            <span
              className={`text-xs font-medium ${
                step >= s.num ? "text-slate-700" : "text-slate-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-1 mt-[-18px] ${
                step > s.num ? "bg-emerald-500" : "bg-slate-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ─── Step 1: Transaction Type ──────────────────────────────────────────────

  const renderStep1 = () => (
    <div>
      <p className="text-sm text-slate-500 mb-4">
        What type of real estate transaction is this?
      </p>
      <div className="grid grid-cols-2 gap-3">
        {TRANSACTION_TYPES.map((t) => {
          const Icon = t.icon;
          const selected = transactionType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTransactionType(t.value)}
              className="flex items-start gap-3 p-4 rounded-xl text-left transition-all"
              style={{
                border: selected
                  ? "2px solid #198754"
                  : "2px solid #e2e8f0",
                backgroundColor: selected ? "#f0fdf4" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!selected)
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                if (!selected)
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "transparent";
              }}
            >
              <div
                className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: selected ? "#dcfce7" : "#f1f5f9",
                }}
              >
                <Icon
                  className="h-5 w-5"
                  style={{ color: selected ? "#198754" : "#64748b" }}
                />
              </div>
              <div>
                <p
                  className="text-sm font-semibold"
                  style={{ color: selected ? "#198754" : "#334155" }}
                >
                  {t.label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ─── Step 2: Jurisdiction ──────────────────────────────────────────────────

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Country
        </label>
        <SearchableSelect
          options={COUNTRY_OPTIONS}
          value={country}
          onChange={(val) => setCountry(val || "CA")}
          placeholder="Select country"
        />
      </div>

      {country === "CA" && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Province / Territory
          </label>
          <SearchableSelect
            options={CA_PROVINCES}
            value={province}
            onChange={(val) => setProvince(val || "")}
            placeholder="Select province"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Municipality
          <span className="text-slate-400 font-normal ml-1">(optional)</span>
        </label>
        <input
          type="text"
          value={municipality}
          onChange={(e) => setMunicipality(e.target.value)}
          placeholder="e.g. Toronto  -  triggers city-specific checklist items"
          className={INPUT_CLASS}
        />
      </div>

      {country === "CA" && province && province !== "ON" && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <MapPin className="h-4 w-4 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800">
              Non-Ontario Jurisdiction
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Note: Ontario-specific checklist items will be hidden for this
              jurisdiction.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Step 3: Property Details ──────────────────────────────────────────────

  const renderStep3 = () => (
    <div className="space-y-4">
      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Street Address *
        </label>
        <input
          type="text"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          placeholder="123 Main Street"
          className={INPUT_CLASS}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Address Line 2
        </label>
        <input
          type="text"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          placeholder="Unit, Suite, Floor"
          className={INPUT_CLASS}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            City
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Province
          </label>
          <input
            type="text"
            value={province}
            disabled
            className={`${INPUT_CLASS} bg-slate-50 text-slate-500`}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Postal Code
          </label>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="A1A 1A1"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* PINs */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700">
            Property Identification Number(s)
          </label>
          <button
            type="button"
            onClick={addPin}
            className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
          >
            <Plus className="h-3.5 w-3.5" /> Add PIN
          </button>
        </div>
        <div className="space-y-2">
          {pins.map((pin, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={pin}
                onChange={(e) => updatePin(i, e.target.value)}
                placeholder={`PIN ${i + 1}`}
                className={INPUT_CLASS}
              />
              <button
                type="button"
                onClick={() => removePin(i)}
                className="p-2.5 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Legal Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Legal Description
        </label>
        <textarea
          value={legalDescription}
          onChange={(e) => setLegalDescription(e.target.value)}
          placeholder="Part of Lot 12, Plan 43R-12345..."
          rows={3}
          className={INPUT_CLASS + " resize-none"}
        />
      </div>

      {/* Condo Toggle */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">
              Condominium Property
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Enable for condo-specific fields
            </p>
          </div>
          <Toggle
            checked={isCondo}
            onChange={(val) => setIsCondo(val)}
          />
        </div>

        {isCondo && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-3 p-4 bg-slate-50 rounded-xl"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Condo Corporation Name
                </label>
                <input
                  type="text"
                  value={condoCorp.name}
                  onChange={(e) =>
                    setCondoCorp({ ...condoCorp, name: e.target.value })
                  }
                  placeholder="YCC 1234"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unit Number
                </label>
                <input
                  type="text"
                  value={condoCorp.unitNumber}
                  onChange={(e) =>
                    setCondoCorp({ ...condoCorp, unitNumber: e.target.value })
                  }
                  placeholder="Unit 1205"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Parking Unit
                </label>
                <input
                  type="text"
                  value={condoCorp.parkingUnit}
                  onChange={(e) =>
                    setCondoCorp({ ...condoCorp, parkingUnit: e.target.value })
                  }
                  placeholder="P1-45"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Locker Unit
                </label>
                <input
                  type="text"
                  value={condoCorp.lockerUnit}
                  onChange={(e) =>
                    setCondoCorp({ ...condoCorp, lockerUnit: e.target.value })
                  }
                  placeholder="L-12"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Property Manager Name
                </label>
                <input
                  type="text"
                  value={condoCorp.propertyManagerName}
                  onChange={(e) =>
                    setCondoCorp({
                      ...condoCorp,
                      propertyManagerName: e.target.value,
                    })
                  }
                  placeholder="Name"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Manager Email
                </label>
                <input
                  type="email"
                  value={condoCorp.propertyManagerEmail}
                  onChange={(e) =>
                    setCondoCorp({
                      ...condoCorp,
                      propertyManagerEmail: e.target.value,
                    })
                  }
                  placeholder="manager@condo.com"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Manager Phone
                </label>
                <input
                  type="tel"
                  value={condoCorp.propertyManagerPhone}
                  onChange={(e) =>
                    setCondoCorp({
                      ...condoCorp,
                      propertyManagerPhone: e.target.value,
                    })
                  }
                  placeholder="+1 (555) 000-0000"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );

  // ─── Step 4: Parties ───────────────────────────────────────────────────────

  const renderStep4 = () => {
    const selectedClientIds = clientParties.map((p) => p.clientId);
    const availableClients = clients.filter(
      (c: any) => !selectedClientIds.includes(c._id)
    );

    return (
      <div className="space-y-4">
        {/* Client Parties */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Clients on Transaction *
          </label>

          {clientParties.map((party, i) => (
            <div
              key={i}
              className="flex items-start gap-3 mb-3 p-3 bg-slate-50 rounded-xl"
            >
              <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="h-4 w-4 text-emerald-700" />
              </div>
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-slate-800">
                  {getClientName(party.clientId)}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <SearchableSelect
                    options={ROLE_OPTIONS}
                    value={party.role}
                    onChange={(val) =>
                      updateClientParty(i, "role", val || "purchaser")
                    }
                    placeholder="Role"
                  />
                  <input
                    type="text"
                    value={party.capacityOnTitle}
                    onChange={(e) =>
                      updateClientParty(i, "capacityOnTitle", e.target.value)
                    }
                    placeholder="Capacity on title"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeClientParty(i)}
                className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <div className="mt-2">
            <SearchableSelect
              options={availableClients.map((c: any) => ({
                value: c._id,
                label: c.displayName,
              }))}
              value={null}
              onChange={(val) => {
                if (val) addClientParty(val);
              }}
              placeholder={
                clientParties.length === 0
                  ? "Select a client..."
                  : "Add another client..."
              }
              clearable
              addNew={{
                label: "Create Client",
                onAdd: () => setShowQuickClient(true),
              }}
            />
          </div>
        </div>

        {/* Other Side Counsel */}
        <div className="border-t border-slate-100 pt-4 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Scale className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Other Side Counsel
            </h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Firm Name
              </label>
              <input
                type="text"
                value={otherSideCounsel.firmName}
                onChange={(e) =>
                  setOtherSideCounsel({
                    ...otherSideCounsel,
                    firmName: e.target.value,
                  })
                }
                placeholder="Law firm name"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Lawyer Name
              </label>
              <input
                type="text"
                value={otherSideCounsel.lawyerName}
                onChange={(e) =>
                  setOtherSideCounsel({
                    ...otherSideCounsel,
                    lawyerName: e.target.value,
                  })
                }
                placeholder="Lawyer name"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={otherSideCounsel.email}
                onChange={(e) =>
                  setOtherSideCounsel({
                    ...otherSideCounsel,
                    email: e.target.value,
                  })
                }
                placeholder="counsel@example.com"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={otherSideCounsel.phone}
                onChange={(e) =>
                  setOtherSideCounsel({
                    ...otherSideCounsel,
                    phone: e.target.value,
                  })
                }
                placeholder="+1 (555) 000-0000"
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </div>

        {/* Realtor */}
        <div className="border-t border-slate-100 pt-4 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">Realtor</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={realtor.name}
                onChange={(e) =>
                  setRealtor({ ...realtor, name: e.target.value })
                }
                placeholder="Realtor name"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Brokerage
              </label>
              <input
                type="text"
                value={realtor.brokerage}
                onChange={(e) =>
                  setRealtor({ ...realtor, brokerage: e.target.value })
                }
                placeholder="Brokerage name"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={realtor.email}
                onChange={(e) =>
                  setRealtor({ ...realtor, email: e.target.value })
                }
                placeholder="realtor@example.com"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={realtor.phone}
                onChange={(e) =>
                  setRealtor({ ...realtor, phone: e.target.value })
                }
                placeholder="+1 (555) 000-0000"
                className={INPUT_CLASS}
              />
            </div>
          </div>
        </div>

        {/* Lender (conditional) */}
        {showLender && (
          <div className="border-t border-slate-100 pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Landmark className="h-4 w-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-700">Lender</h4>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Lender Name
                </label>
                <input
                  type="text"
                  value={lender.name}
                  onChange={(e) =>
                    setLender({ ...lender, name: e.target.value })
                  }
                  placeholder="Bank or institution name"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Branch Transit
                </label>
                <input
                  type="text"
                  value={lender.branchTransit}
                  onChange={(e) =>
                    setLender({ ...lender, branchTransit: e.target.value })
                  }
                  placeholder="00000"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Step 5: Key Dates & APS Terms ─────────────────────────────────────────

  const renderStep5 = () => (
    <div className="space-y-4">
      {/* Key Dates */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-4 w-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-700">Key Dates</h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Closing Date
            </label>
            <input
              type="date"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
              className={INPUT_CLASS}
            />
            {closingDate && (
              <p className="text-xs text-slate-500 mt-1">
                This will be set as the matter due date
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              APS Date
            </label>
            <input
              type="date"
              value={apsDate}
              onChange={(e) => setApsDate(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Requisition Date
            </label>
            <input
              type="date"
              value={requisitionDate}
              onChange={(e) => setRequisitionDate(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Possession Date
            </label>
            <input
              type="date"
              value={possessionDate}
              onChange={(e) => setPossessionDate(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </div>

      {/* APS Financial Terms */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="h-4 w-4 text-slate-500" />
          <h4 className="text-sm font-semibold text-slate-700">
            APS Financial Terms
          </h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Purchase Price
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={purchasePriceDollars}
                onChange={(e) => setPurchasePriceDollars(e.target.value)}
                placeholder="0.00"
                className={INPUT_CLASS + " pl-8"}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Deposit Amount
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                $
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={depositAmountDollars}
                onChange={(e) => setDepositAmountDollars(e.target.value)}
                placeholder="0.00"
                className={INPUT_CLASS + " pl-8"}
              />
            </div>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Deposit Held By
          </label>
          <SearchableSelect
            options={DEPOSIT_HELD_BY_OPTIONS}
            value={depositHeldBy || null}
            onChange={(val) => setDepositHeldBy(val || "")}
            placeholder="Select..."
            clearable
          />
        </div>
      </div>

      {/* Conditions */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Conditions
            </h4>
          </div>
          <button
            type="button"
            onClick={addCondition}
            className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
          >
            <Plus className="h-3.5 w-3.5" /> Add Condition
          </button>
        </div>
        {conditions.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-3">
            No conditions added yet
          </p>
        ) : (
          <div className="space-y-2">
            {conditions.map((cond, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={cond.label}
                  onChange={(e) =>
                    updateCondition(i, "label", e.target.value)
                  }
                  placeholder="Condition description"
                  className={INPUT_CLASS + " flex-1"}
                />
                <button
                  type="button"
                  onClick={() =>
                    updateCondition(i, "waived", !cond.waived)
                  }
                  className={`px-3 py-2.5 rounded-xl text-xs font-medium border transition-colors flex-shrink-0 ${
                    cond.waived
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-slate-50 border-slate-200 text-slate-500"
                  }`}
                >
                  {cond.waived ? "Waived" : "Active"}
                </button>
                <button
                  type="button"
                  onClick={() => removeCondition(i)}
                  className="p-2.5 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ─── Step 6: Financial Setup ───────────────────────────────────────────────

  const renderStep6 = () => (
    <div className="space-y-4">
      {/* LTT Estimate */}
      {province === "ON" && purchasePriceCents > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Land Transfer Tax Estimate
            </h4>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">
                Ontario Provincial LTT
              </span>
              <span className="text-sm font-semibold text-slate-800">
                {formatCAD(ontarioLTT)}
              </span>
            </div>

            {torontoMLTT > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">
                  Toronto Municipal LTT
                </span>
                <span className="text-sm font-semibold text-slate-800">
                  {formatCAD(torontoMLTT)}
                </span>
              </div>
            )}

            {firstTimeHomeBuyer && provincialRebate > 0 && (
              <>
                <div className="flex items-center justify-between text-emerald-600">
                  <span className="text-sm">Provincial FTHB Rebate</span>
                  <span className="text-sm font-semibold">
                    -{formatCAD(provincialRebate)}
                  </span>
                </div>
                {municipalRebate > 0 && (
                  <div className="flex items-center justify-between text-emerald-600">
                    <span className="text-sm">Municipal FTHB Rebate</span>
                    <span className="text-sm font-semibold">
                      -{formatCAD(municipalRebate)}
                    </span>
                  </div>
                )}
              </>
            )}

            <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">
                Total LTT
              </span>
              <span className="text-base font-bold text-slate-900">
                {formatCAD(
                  ontarioLTT +
                    torontoMLTT -
                    provincialRebate -
                    municipalRebate
                )}
              </span>
            </div>
          </div>

          {/* First Time Home Buyer Toggle */}
          <div className="flex items-center justify-between mt-3 p-3 bg-emerald-50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-slate-700">
                First Time Home Buyer
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Provincial rebate up to $4,000 / Municipal up to $4,475
              </p>
            </div>
            <Toggle
              checked={firstTimeHomeBuyer}
              onChange={(val) => setFirstTimeHomeBuyer(val)}
            />
          </div>
        </div>
      )}

      {province === "ON" && purchasePriceCents === 0 && (
        <div className="p-4 bg-slate-50 rounded-xl text-center">
          <DollarSign className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            Enter a purchase price in the previous step to see LTT estimates.
          </p>
        </div>
      )}

      {province !== "ON" && (
        <div className="p-4 bg-slate-50 rounded-xl text-center">
          <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            LTT calculations are currently available for Ontario only.
          </p>
        </div>
      )}

      {/* Trust Classification */}
      <div className="border-t border-slate-100 pt-4 mt-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Trust Classification
        </label>
        <SearchableSelect
          options={TRUST_CLASSIFICATION_OPTIONS}
          value={trustClassification || null}
          onChange={(val) => setTrustClassification(val || "")}
          placeholder="Select classification..."
          clearable
        />
      </div>

      {/* Holdback Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Holdback Notes
        </label>
        <textarea
          value={holdbackNotes}
          onChange={(e) => setHoldbackNotes(e.target.value)}
          placeholder="Any known holdbacks or notes..."
          rows={3}
          className={INPUT_CLASS + " resize-none"}
        />
      </div>
    </div>
  );

  // ─── Step 7: Checklist & Billing ───────────────────────────────────────────

  const toggleChecklistItem = (key: string) => {
    setChecklistItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, enabled: !item.enabled } : item))
    );
    // Clear invoice trigger if it was disabled
    if (invoiceTriggerKey === key) {
      const item = checklistItems.find((i) => i.key === key);
      if (item?.enabled) setInvoiceTriggerKey("");
    }
  };

  const removeChecklistItem = (key: string) => {
    setChecklistItems((prev) => prev.filter((item) => item.key !== key));
    if (invoiceTriggerKey === key) setInvoiceTriggerKey("");
  };

  const addCustomItem = () => {
    if (!newItemLabel.trim()) return;
    const key = `custom_${Date.now()}`;
    setChecklistItems((prev) => [
      ...prev,
      { key, label: newItemLabel.trim(), required: false, source: newItemSource, enabled: true, isCustom: true },
    ]);
    setNewItemLabel("");
  };

  const toggleSourceGroup = (source: string) => {
    const items = checklistItems.filter((i) => i.source === source);
    const allEnabled = items.every((i) => i.enabled);
    setChecklistItems((prev) =>
      prev.map((item) => (item.source === source ? { ...item, enabled: !allEnabled } : item))
    );
  };

  const renderSourceGroup = (source: "client" | "other_side" | "firm") => {
    const config = SOURCE_CONFIG[source];
    const Icon = config.Icon;
    const items = checklistItems.filter((i) => i.source === source);
    if (items.length === 0) return null;
    const enabledCount = items.filter((i) => i.enabled).length;
    const collapsed = collapsedSources[source] ?? false;

    return (
      <div key={source} className={`rounded-xl border ${config.border} overflow-hidden`}>
        <button
          type="button"
          onClick={() => setCollapsedSources((prev) => ({ ...prev, [source]: !collapsed }))}
          className={`w-full flex items-center justify-between px-4 py-3 ${config.bg} transition-colors`}
        >
          <div className="flex items-center gap-2.5">
            <Icon className={`h-4 w-4 ${config.color}`} />
            <span className={`text-sm font-semibold ${config.color}`}>{config.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.badge}`}>
              {enabledCount}/{items.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleSourceGroup(source); }}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-white/60 transition-colors"
            >
              {items.every((i) => i.enabled) ? "Deselect All" : "Select All"}
            </button>
            <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${!collapsed ? "rotate-90" : ""}`} />
          </div>
        </button>

        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 py-2 space-y-1">
                {items.map((item) => (
                  <motion.div
                    key={item.key}
                    layout
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group ${
                      item.enabled ? "bg-white" : "bg-slate-50 opacity-60"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleChecklistItem(item.key)}
                      className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        item.enabled
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {item.enabled && <Check className="h-3 w-3" />}
                    </button>
                    <span className={`text-sm flex-1 ${item.enabled ? "text-slate-700" : "text-slate-400 line-through"}`}>
                      {item.label}
                    </span>
                    {item.required && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-red-400 flex-shrink-0">
                        Required
                      </span>
                    )}
                    {item.isCustom && (
                      <button
                        type="button"
                        onClick={() => removeChecklistItem(item.key)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-all flex-shrink-0"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const TIMING_OPTIONS: { value: InvoiceTiming; label: string; description: string; icon: React.FC<any> }[] = [
    { value: "now", label: "Send Immediately", description: "Invoice will be emailed to the client right after matter creation", icon: Send },
    { value: "manual", label: "Send After Closing", description: "Invoice created as draft — send manually or after closing", icon: Clock },
    { value: "on_document_received", label: "Send When Document Received", description: "Auto-send when a specific document is marked as received", icon: FileCheck },
  ];

  const DUE_DATE_PILLS = [
    { value: "0", label: "On Receipt" },
    { value: "15", label: "Net 15" },
    { value: "30", label: "Net 30" },
    { value: "custom", label: "Custom" },
  ] as const;

  const renderStep7 = () => (
    <div className="space-y-6">
      {/* ── Section A: Document Checklist ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4.5 w-4.5 text-emerald-600" />
            <h3 className="text-sm font-bold text-slate-800">Document Checklist</h3>
            <span className="text-xs text-slate-400 font-medium">
              {enabledChecklistItems.length} of {checklistItems.length} selected
            </span>
          </div>
        </div>

        {!checklistLoaded && checklistItems.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              No checklist template found for this transaction type and jurisdiction.
            </p>
            <p className="text-xs text-slate-400 mt-1">
              You can add custom items below.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(["client", "other_side", "firm"] as const).map((source) => renderSourceGroup(source))}
          </div>
        )}

        {/* Add custom item */}
        <div className="mt-3 flex items-center gap-2">
          <input
            type="text"
            value={newItemLabel}
            onChange={(e) => setNewItemLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustomItem()}
            placeholder="Add a custom document..."
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <select
            value={newItemSource}
            onChange={(e) => setNewItemSource(e.target.value as any)}
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="client">Client</option>
            <option value="other_side">Other Side</option>
            <option value="firm">Firm</option>
          </select>
          <button
            type="button"
            onClick={addCustomItem}
            disabled={!newItemLabel.trim()}
            className="flex items-center gap-1 px-3 py-2.5 rounded-xl text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="border-t border-slate-100" />

      {/* ── Section B: Invoice Setup ── */}
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="h-4.5 w-4.5 text-emerald-600" />
            <h3 className="text-sm font-bold text-slate-800">Initial Invoice</h3>
          </div>
          <Toggle checked={createInvoice} onChange={setCreateInvoice} />
        </div>
        <p className="text-xs text-slate-400 mt-1 mb-3">
          Create an invoice for this matter during setup
        </p>

        <AnimatePresence>
          {createInvoice && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="space-y-4 pt-1">
                {/* Fee Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Legal Fee
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoiceFeeDollars}
                      onChange={(e) => setInvoiceFeeDollars(e.target.value)}
                      placeholder="0.00"
                      className={INPUT_CLASS + " pl-8"}
                    />
                  </div>

                  {/* Tax breakdown */}
                  {invoiceFeeCents > 0 && taxConfig.taxMode !== "none" && (
                    <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2.5 space-y-1">
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Subtotal</span>
                        <span>{formatCAD(invoiceFeeCents)}</span>
                      </div>
                      {taxConfig.taxMode === "gst_pst" ? (
                        <>
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>GST ({taxConfig.gstRate}%)</span>
                            <span>{formatCAD(invoiceTaxBreakdown.gstCents)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>PST ({taxConfig.pstRate}%)</span>
                            <span>{formatCAD(invoiceTaxBreakdown.pstCents)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{invoiceTaxLabel}</span>
                          <span>{formatCAD(invoiceTaxBreakdown.totalTaxCents)}</span>
                        </div>
                      )}
                      <div className="border-t border-slate-200 pt-1 flex items-center justify-between text-sm font-semibold text-slate-700">
                        <span>Total</span>
                        <span>{formatCAD(invoiceTotalCents)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Invoice Timing */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    When to send
                  </label>
                  <div className="space-y-2">
                    {TIMING_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      const selected = invoiceTiming === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setInvoiceTiming(opt.value)}
                          className={`w-full flex items-start gap-3 p-3.5 rounded-xl text-left transition-all ${
                            selected
                              ? "bg-emerald-50/70 border-l-[3px] border-emerald-500 pl-[calc(0.875rem-1px)]"
                              : "bg-white border-l-[3px] border-transparent hover:bg-slate-50 pl-[calc(0.875rem-1px)]"
                          }`}
                          style={{ border: selected ? undefined : "1px solid #e2e8f0", borderLeft: selected ? "3px solid #10b981" : "3px solid transparent" }}
                        >
                          <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            selected ? "bg-emerald-100" : "bg-slate-100"
                          }`}>
                            <Icon className={`h-4 w-4 ${selected ? "text-emerald-600" : "text-slate-400"}`} />
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-semibold ${selected ? "text-emerald-800" : "text-slate-700"}`}>
                              {opt.label}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
                          </div>
                          <div className={`ml-auto flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                            selected ? "border-emerald-500 bg-emerald-500" : "border-slate-300"
                          }`}>
                            {selected && <Check className="h-3 w-3 text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Document trigger selector */}
                <AnimatePresence>
                  {invoiceTiming === "on_document_received" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Send invoice when this document is received:
                        </label>
                        <SearchableSelect
                          options={enabledChecklistItems.map((item) => ({
                            value: item.key,
                            label: `${item.label} (${SOURCE_CONFIG[item.source].label})`,
                          }))}
                          value={invoiceTriggerKey || null}
                          onChange={(val) => setInvoiceTriggerKey(val || "")}
                          placeholder="Select a checklist document..."
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Payment terms
                  </label>
                  <div className="flex items-center gap-2">
                    {DUE_DATE_PILLS.map((pill) => (
                      <button
                        key={pill.value}
                        type="button"
                        onClick={() => setDueDateOption(pill.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          dueDateOption === pill.value
                            ? "bg-emerald-600 text-white shadow-sm"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {pill.label}
                      </button>
                    ))}
                  </div>
                  {dueDateOption === "custom" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="mt-2"
                    >
                      <input
                        type="date"
                        value={customDueDate}
                        onChange={(e) => setCustomDueDate(e.target.value)}
                        className={INPUT_CLASS}
                      />
                    </motion.div>
                  )}
                </div>

                {/* Invoice Summary */}
                {invoiceFeeCents > 0 && (
                  <div className="p-3 bg-emerald-50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">
                          {formatCAD(invoiceTotalCents)}
                        </span>
                        {invoiceTaxBreakdown.totalTaxCents > 0 && (
                          <span className="text-xs text-emerald-600">
                            (incl. {invoiceTaxLabel}: {formatCAD(invoiceTaxBreakdown.totalTaxCents)})
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-emerald-600">
                        {invoiceTiming === "now" ? "Send immediately" : invoiceTiming === "on_document_received" ? `On "${triggerItem?.label || "..."}"` : "Draft — send after closing"}
                        {" · "}
                        {dueDateOption === "0" ? "Due on receipt" : dueDateOption === "custom" ? `Due ${customDueDate}` : `Net ${dueDateOption}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  // ─── Step 8: Review & Create ───────────────────────────────────────────────

  const renderReviewSection = (
    key: string,
    title: string,
    icon: React.FC<any>,
    content: React.ReactNode
  ) => {
    const Icon = icon;
    const expanded = expandedSections[key];
    return (
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-semibold text-slate-700">
              {title}
            </span>
          </div>
          <ChevronRight
            className={`h-4 w-4 text-slate-400 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          />
        </button>
        {expanded && <div className="px-4 py-3 space-y-1">{content}</div>}
      </div>
    );
  };

  const ReviewRow = ({
    label,
    value,
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => (
    <div className="flex items-start justify-between py-1">
      <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
      <span className="text-xs font-medium text-slate-700 text-right ml-4">
        {value || <span className="text-slate-400 italic">Not set</span>}
      </span>
    </div>
  );

  const transactionLabel =
    TRANSACTION_TYPES.find((t) => t.value === transactionType)?.label || "";
  const provinceLabel =
    CA_PROVINCES.find((p) => p.value === province)?.label || province;

  const renderStep8 = () => (
    <div className="space-y-3">
      <p className="text-sm text-slate-500 mb-2">
        Review all details before creating the matter.
      </p>

      {renderReviewSection("type", "Transaction Type", Home, (
        <ReviewRow label="Type" value={transactionLabel} />
      ))}

      {renderReviewSection("jurisdiction", "Jurisdiction", MapPin, (
        <>
          <ReviewRow
            label="Country"
            value={COUNTRY_OPTIONS.find((c) => c.value === country)?.label || country}
          />
          <ReviewRow label="Province" value={provinceLabel} />
          {municipality && (
            <ReviewRow label="Municipality" value={municipality} />
          )}
        </>
      ))}

      {renderReviewSection("property", "Property", Building2, (
        <>
          <ReviewRow
            label="Address"
            value={[addressLine1, addressLine2, city, province, postalCode]
              .filter(Boolean)
              .join(", ")}
          />
          {pins.filter((p) => p.trim()).length > 0 && (
            <ReviewRow
              label="PIN(s)"
              value={pins.filter((p) => p.trim()).join(", ")}
            />
          )}
          {legalDescription && (
            <ReviewRow label="Legal Description" value={legalDescription} />
          )}
          <ReviewRow label="Condominium" value={isCondo ? "Yes" : "No"} />
          {isCondo && condoCorp.name && (
            <ReviewRow label="Condo Corp" value={condoCorp.name} />
          )}
          {isCondo && condoCorp.unitNumber && (
            <ReviewRow label="Unit" value={condoCorp.unitNumber} />
          )}
        </>
      ))}

      {renderReviewSection("parties", "Parties", Users, (
        <>
          {clientParties.map((p, i) => (
            <ReviewRow
              key={i}
              label={ROLE_OPTIONS.find((r) => r.value === p.role)?.label || p.role}
              value={
                <span>
                  {getClientName(p.clientId)}
                  {p.capacityOnTitle && (
                    <span className="text-slate-400 ml-1">
                      ({p.capacityOnTitle})
                    </span>
                  )}
                </span>
              }
            />
          ))}
          {otherSideCounsel.firmName && (
            <ReviewRow
              label="Opposing Counsel"
              value={`${otherSideCounsel.lawyerName} - ${otherSideCounsel.firmName}`}
            />
          )}
          {realtor.name && (
            <ReviewRow
              label="Realtor"
              value={
                realtor.brokerage
                  ? `${realtor.name} (${realtor.brokerage})`
                  : realtor.name
              }
            />
          )}
          {showLender && lender.name && (
            <ReviewRow label="Lender" value={lender.name} />
          )}
        </>
      ))}

      {renderReviewSection("dates", "Dates & APS Terms", Calendar, (
        <>
          {closingDate && <ReviewRow label="Closing Date" value={closingDate} />}
          {apsDate && <ReviewRow label="APS Date" value={apsDate} />}
          {requisitionDate && (
            <ReviewRow label="Requisition Date" value={requisitionDate} />
          )}
          {possessionDate && (
            <ReviewRow label="Possession Date" value={possessionDate} />
          )}
          {purchasePriceCents > 0 && (
            <ReviewRow
              label="Purchase Price"
              value={formatCAD(purchasePriceCents)}
            />
          )}
          {depositAmountCents > 0 && (
            <ReviewRow
              label="Deposit"
              value={formatCAD(depositAmountCents)}
            />
          )}
          {depositHeldBy && (
            <ReviewRow
              label="Deposit Held By"
              value={
                DEPOSIT_HELD_BY_OPTIONS.find((d) => d.value === depositHeldBy)
                  ?.label || depositHeldBy
              }
            />
          )}
          {conditions.filter((c) => c.label.trim()).length > 0 && (
            <ReviewRow
              label="Conditions"
              value={`${conditions.filter((c) => c.label.trim()).length} condition(s)`}
            />
          )}
        </>
      ))}

      {renderReviewSection("financial", "Financial", DollarSign, (
        <>
          {province === "ON" && purchasePriceCents > 0 && (
            <>
              <ReviewRow
                label="Ontario LTT"
                value={formatCAD(ontarioLTT)}
              />
              {torontoMLTT > 0 && (
                <ReviewRow
                  label="Toronto MLTT"
                  value={formatCAD(torontoMLTT)}
                />
              )}
              <ReviewRow
                label="First Time Buyer"
                value={firstTimeHomeBuyer ? "Yes" : "No"}
              />
              {firstTimeHomeBuyer && (
                <ReviewRow
                  label="Net LTT"
                  value={formatCAD(
                    ontarioLTT +
                      torontoMLTT -
                      provincialRebate -
                      municipalRebate
                  )}
                />
              )}
            </>
          )}
          {trustClassification && (
            <ReviewRow
              label="Trust Classification"
              value={
                TRUST_CLASSIFICATION_OPTIONS.find(
                  (t) => t.value === trustClassification
                )?.label || trustClassification
              }
            />
          )}
          {holdbackNotes && (
            <ReviewRow label="Holdback Notes" value={holdbackNotes} />
          )}
        </>
      ))}

      {/* ── Document Checklist Review ── */}
      {renderReviewSection("checklist", "Document Checklist", ClipboardList, (
        <>
          <div className="flex items-center justify-between">
            <ReviewRow
              label="Documents"
              value={`${enabledChecklistItems.length} items selected`}
            />
            <button
              type="button"
              onClick={() => goToStep(7)}
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              Edit
            </button>
          </div>
          {Object.entries(checklistBySource).map(([source, items]) =>
            items.length > 0 ? (
              <ReviewRow
                key={source}
                label={SOURCE_CONFIG[source as keyof typeof SOURCE_CONFIG].label}
                value={`${items.length} item${items.length !== 1 ? "s" : ""}`}
              />
            ) : null
          )}
        </>
      ))}

      {/* ── Invoice Review ── */}
      {renderReviewSection("invoice", "Invoice", Receipt, (
        <>
          {createInvoice && invoiceFeeCents > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <ReviewRow label="Legal Fee" value={formatCAD(invoiceFeeCents)} />
                <button
                  type="button"
                  onClick={() => goToStep(7)}
                  className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  Edit
                </button>
              </div>
              {invoiceTaxBreakdown.totalTaxCents > 0 && (
                <ReviewRow label={invoiceTaxLabel} value={formatCAD(invoiceTaxBreakdown.totalTaxCents)} />
              )}
              <ReviewRow label="Total" value={
                <span className="font-semibold">{formatCAD(invoiceTotalCents)}</span>
              } />
              <ReviewRow
                label="Timing"
                value={
                  invoiceTiming === "now"
                    ? "Send immediately"
                    : invoiceTiming === "on_document_received"
                    ? `When "${triggerItem?.label || "..."}" received`
                    : "Draft — send after closing"
                }
              />
              <ReviewRow
                label="Due"
                value={
                  dueDateOption === "0"
                    ? "On receipt"
                    : dueDateOption === "custom"
                    ? customDueDate
                    : `Net ${dueDateOption} days`
                }
              />
            </>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400 italic">No invoice</span>
              <button
                type="button"
                onClick={() => goToStep(7)}
                className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
              >
                Edit
              </button>
            </div>
          )}
        </>
      ))}

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm whitespace-pre-line">
          {error}
        </div>
      )}
    </div>
  );

  // ─── Render Step Content ───────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      case 8:
        return renderStep8();
      default:
        return null;
    }
  };

  // ─── Footer ────────────────────────────────────────────────────────────────

  const renderFooter = () => (
    <div className="flex items-center justify-between">
      <div>
        {step === 1 ? (
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        )}
      </div>
      <div>
        {step < 8 ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canProceed()}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "#0F5132" }}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "#0F5132" }}
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Home className="h-4 w-4" />
                Create Real Estate Matter
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  // ─── Subtitle per step ─────────────────────────────────────────────────────

  const stepSubtitles: Record<number, string> = {
    1: "Select the type of real estate transaction",
    2: "Where is the property located?",
    3: "Enter the property address and details",
    4: "Add clients and other parties to this transaction",
    5: "Set closing dates and agreement of purchase and sale terms",
    6: "Review land transfer tax and trust setup",
    7: "Review your document checklist and set up billing",
    8: "Review all details and create the matter",
  };

  // ─── Main Render ───────────────────────────────────────────────────────────

  return (
    <>
      <PremiumModal
        open={true}
        onClose={onClose}
        title={`New Real Estate Matter`}
        subtitle={stepSubtitles[step]}
        icon={Home}
        size="xl"
        footer={renderFooter()}
      >
        {renderProgressBar()}

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </PremiumModal>

      <QuickCreateClientModal
        open={showQuickClient}
        onClose={() => setShowQuickClient(false)}
        onCreated={(newClient) => {
          setClients((prev: any[]) => [...prev, newClient]);
          addClientParty(newClient._id);
          setShowQuickClient(false);
        }}
      />
    </>
  );
};

export default CreateRealEstateMatterWizard;
